import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './Organizations.css';

const LICENSE_TIERS = {
  up_to_2000: 'Up to 2,000 students',
  '2001_to_5000': '2,001-5,000 students',
  '5001_to_10000': '5,001-10,000 students',
  '10001_to_20000': '10,001-20,000 students',
  '20000_plus': '20,000+ students (Custom)',
};

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query all organizations
      const orgsRef = collection(db, 'organizations');
      const orgsSnapshot = await getDocs(orgsRef);
      
      // Sort by createdAt if available, otherwise by domain
      const sortedOrgs = [];
      orgsSnapshot.forEach((doc) => {
        sortedOrgs.push({ doc, data: doc.data() });
      });
      
      sortedOrgs.sort((a, b) => {
        const aDate = a.data.createdAt?.toDate ? a.data.createdAt.toDate() : (a.data.createdAt instanceof Date ? a.data.createdAt : null);
        const bDate = b.data.createdAt?.toDate ? b.data.createdAt.toDate() : (b.data.createdAt instanceof Date ? b.data.createdAt : null);
        if (aDate && bDate) {
          return bDate - aDate; // Descending order
        }
        if (aDate) return -1;
        if (bDate) return 1;
        return a.doc.id.localeCompare(b.doc.id); // Fallback to domain name
      });

      const orgsData = [];
      sortedOrgs.forEach(({ doc, data }) => {
        orgsData.push({
          id: doc.id,
          domain: doc.id,
          name: data.name || doc.id,
          adminId: data.adminId || null,
          admins: data.admins || [],
          orgType: data.orgType || 'university',
          billingContactEmail: data.billing_contact_email || null,
          stripeCustomerId: data.stripe_customer_id || null,
          stripeSubscriptionId: data.stripe_subscription_id || null,
          licenseTier: data.license_tier || null,
          termSemester: data.term_semester || null,
          termYear: data.term_year || null,
          termEndDate: data.term_end_date?.toDate() || null,
          billingSetupAt: data.billing_setup_at?.toDate() || null,
          promoCode: data.promo_code || null,
          renewalOptedOut: data.renewal_opted_out || false,
          memberCount: data.memberCount || null,
          memberCountVerified: data.memberCountVerified || false,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Date ? data.createdAt : null),
        });
      });

      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getBillingStatus = (org) => {
    if (!org.stripeCustomerId || !org.stripeSubscriptionId) {
      return { status: 'not_setup', label: 'Not Set Up', color: '#999' };
    }

    if (org.renewalOptedOut) {
      return { status: 'opted_out', label: 'Opted Out', color: '#f44336' };
    }

    if (!org.termEndDate) {
      return { status: 'no_term', label: 'No Term Set', color: '#ff9800' };
    }

    const now = new Date();
    const termEnd = org.termEndDate;
    const daysUntilEnd = Math.floor((termEnd - now) / (1000 * 60 * 60 * 24));

    if (daysUntilEnd < 0) {
      const daysPastEnd = Math.abs(daysUntilEnd);
      if (daysPastEnd > 7) {
        return { status: 'expired', label: 'Expired', color: '#f44336' };
      }
      return { status: 'grace_period', label: 'Grace Period', color: '#ff9800' };
    }

    if (daysUntilEnd <= 30) {
      return { status: 'ending_soon', label: 'Ending Soon', color: '#ff9800' };
    }

    return { status: 'active', label: 'Active', color: '#4caf50' };
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (tier, orgType, memberCount) => {
    // On-campus org pricing
    if (orgType === 'on_campus_org') {
      const ON_CAMPUS_TIERS = {
        '1_to_25': { semester: 75, year: 150 },
        '26_to_50': { semester: 125, year: 250 },
        '51_to_100': { semester: 225, year: 450 },
        '101_to_200': { semester: 350, year: 700 },
      };
      
      // First check if tier is already set (from billing setup)
      if (tier && ON_CAMPUS_TIERS[tier]) {
        const pricing = ON_CAMPUS_TIERS[tier];
        return `$${pricing.semester}/sem ($${pricing.year}/yr)`;
      }
      
      // Otherwise, calculate from member count
      if (!memberCount) return 'N/A';
      
      let tierKey = null;
      if (memberCount <= 25) tierKey = '1_to_25';
      else if (memberCount <= 50) tierKey = '26_to_50';
      else if (memberCount <= 100) tierKey = '51_to_100';
      else if (memberCount <= 200) tierKey = '101_to_200';
      
      if (tierKey && ON_CAMPUS_TIERS[tierKey]) {
        const pricing = ON_CAMPUS_TIERS[tierKey];
        return `$${pricing.semester}/sem ($${pricing.year}/yr)`;
      }
      return 'Custom';
    }
    
    // University pricing
    const prices = {
      up_to_2000: 1000,
      '2001_to_5000': 2000,
      '5001_to_10000': 3000,
      '10001_to_20000': 4500,
    };
    return prices[tier] ? `$${prices[tier].toLocaleString()}/mo` : 'Custom';
  };

  const handleVerifyMemberCount = async (domain, memberCount) => {
    if (!window.confirm(`Verify member count of ${memberCount} for ${domain}?`)) {
      return;
    }

    try {
      const orgRef = doc(db, 'organizations', domain);
      await setDoc(orgRef, {
        memberCountVerified: true,
        memberCountVerifiedAt: new Date(),
      }, { merge: true });
      
      // Reload organizations
      await loadOrganizations();
      alert('Member count verified successfully.');
    } catch (err) {
      console.error('Error verifying member count:', err);
      alert('Failed to verify member count. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="organizations-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organizations-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadOrganizations} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeCount = organizations.filter(
    (org) => getBillingStatus(org).status === 'active'
  ).length;
  const totalCount = organizations.length;
  const billingSetupCount = organizations.filter(
    (org) => org.stripeCustomerId && org.stripeSubscriptionId
  ).length;

  return (
    <div className="organizations-container">
      <div className="organizations-header">
        <h1>Organizations</h1>
        <button onClick={loadOrganizations} className="btn-refresh" disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      <div className="organizations-stats">
        <div className="stat-card">
          <div className="stat-value">{totalCount}</div>
          <div className="stat-label">Total Organizations</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{billingSetupCount}</div>
          <div className="stat-label">Billing Set Up</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{activeCount}</div>
          <div className="stat-label">Active</div>
        </div>
      </div>

      <div className="organizations-table-container">
        <table className="organizations-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Name</th>
              <th>Type</th>
              <th>Billing Status</th>
              <th>License Tier</th>
              <th>Term</th>
              <th>Term End Date</th>
              <th>Billing Contact</th>
              <th>Member Count</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {organizations.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-state">
                  No organizations found
                </td>
              </tr>
            ) : (
              organizations.map((org) => {
                const billingStatus = getBillingStatus(org);
                return (
                  <tr key={org.id}>
                    <td>
                      <strong>{org.domain}</strong>
                    </td>
                    <td>{org.name}</td>
                    <td>
                      <span className={`org-type-badge ${org.orgType === 'on_campus_org' ? 'on-campus' : 'university'}`}>
                        {org.orgType === 'on_campus_org' ? 'On-Campus Org' : 'University/College'}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: billingStatus.color }}
                      >
                        {billingStatus.label}
                      </span>
                    </td>
                    <td>
                      {org.licenseTier ? (
                        <>
                          <div>{LICENSE_TIERS[org.licenseTier] || org.licenseTier}</div>
                          <div className="tier-price">{formatCurrency(org.licenseTier, org.orgType, org.memberCount)}</div>
                        </>
                      ) : (
                        'Not set'
                      )}
                    </td>
                    <td>
                      {org.termSemester && org.termYear
                        ? `${org.termSemester.charAt(0).toUpperCase() + org.termSemester.slice(1)} ${org.termYear}`
                        : 'N/A'}
                    </td>
                    <td>{formatDate(org.termEndDate)}</td>
                    <td>{org.billingContactEmail || 'N/A'}</td>
                    <td>
                      {org.orgType === 'on_campus_org' ? (
                        <div>
                          {org.memberCount ? (
                            <>
                              <div>{org.memberCount} members</div>
                              {org.memberCountVerified ? (
                                <span className="verified-badge">✓ Verified</span>
                              ) : (
                                <div>
                                  <span className="pending-badge">Pending</span>
                                  <button
                                    className="verify-button"
                                    onClick={() => handleVerifyMemberCount(org.domain, org.memberCount)}
                                    style={{ marginLeft: '8px', padding: '4px 8px', fontSize: '12px' }}
                                  >
                                    Verify
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>{formatDate(org.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Organizations;
